import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const chainSteps = [
  { label: '课堂原话', icon: 'ri-mic-line', color: 'primary' },
  { label: '核心概念', icon: 'ri-lightbulb-line', color: 'accent' },
  { label: '问题链', icon: 'ri-question-answer-line', color: 'primary' },
  { label: '案例链', icon: 'ri-stack-line', color: 'accent' },
  { label: '理论链', icon: 'ri-link-m', color: 'primary' },
  { label: '价值链', icon: 'ri-heart-line', color: 'accent' },
];

const cases = [
  {
    code: '高职07',
    title: '问题链正向候选',
    position: '人工两侧头部',
    desc: '适合检验问题、辨析、案例和价值回扣能否形成完整链条',
    status: '具体原话待核',
    accent: true,
  },
  {
    code: '高职02',
    title: '均衡结构候选',
    position: '待补证据',
    desc: '拟用于展示专题聚焦、结构稳定和案例支撑较均衡的课程',
    status: '待补证据',
    accent: false,
  },
  {
    code: '本科10',
    title: '问题链边界候选',
    position: '人工两侧尾部',
    desc: '检验外显问题结构能否掩盖理论展开不足',
    status: 'AI侧轨道分待补',
    accent: true,
  },
];

export default function P28TextUnderstandingNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点二
            </span>
          </div>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            课堂文字理解与<span className="text-primary-600">问题链识别</span>
          </h2>
        </div>

        {/* 流程链 */}
        <div className={`max-w-5xl mx-auto mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
            {chainSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${step.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/30'} transition-all duration-300 hover:scale-105`}>
                  <i className={`${step.icon} text-sm ${step.color === 'primary' ? 'text-primary-500' : 'text-accent-500'}`} />
                  <span className="text-xs font-body font-semibold text-foreground-700 whitespace-nowrap">{step.label}</span>
                </div>
                {i < chainSteps.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-300 mx-0.5 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 三列案例 */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {cases.map((c, i) => (
            <div key={c.code} className={`p-4 rounded-2xl transition-all duration-500 hover:scale-[1.02] ${c.accent ? 'glass-liquid-gold border-accent-200/30' : 'glass-liquid border-primary-200/30'}`} style={{ transitionDelay: `${400 + i * 100}ms` }}>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-sm font-bold text-foreground-800">{c.code}</h3>
                  <span className="text-[10px] font-body font-semibold text-foreground-400">{c.position}</span>
                </div>
                <p className="text-xs font-body font-semibold text-foreground-700 mb-2">{c.title}</p>
                <p className="text-[11px] text-foreground-500 font-body leading-relaxed mb-2">{c.desc}</p>
                <div className="flex items-center gap-1.5 pt-2 border-t border-background-200/60">
                  <i className="ri-information-line text-foreground-400 text-[10px]" />
                  <span className="text-[10px] text-foreground-400 font-body">{c.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 底部数据 */}
        <div className={`mt-5 max-w-3xl mx-auto transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl glass border-white/50">
              <i className="ri-bar-chart-line text-sm text-primary-500" />
              <span className="text-xs font-body text-foreground-500">
                "逻辑强"与人工总分正向差异约<span className="font-bold text-primary-700">+2.7</span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl glass-gold border-accent-200/30">
              <i className="ri-information-line text-sm text-accent-500" />
              <span className="text-xs font-body text-foreground-500">
                结构清楚有价值，但不能替代课堂整体完成度
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}